import React, { useEffect, useState } from "react";
import axios from "axios";
import EventEHRLazyDropdown from "../../../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import CodeDialog from "./dialogs/CodeDialog";
import { filterMasterCodeData } from "../constants";
import { getToken } from "../../../utils/getToken";
import { searchIcon } from "../../../assets";
import { eventEHRCustomToastMessage } from "../../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";

interface DiagnosisCodeSelectProps {
  // The diagnosis-table state lives in the parent (it's rendered there and
  // sent on save); we only need its setter to add a selected code.
  setSelectedDiagnosis: React.Dispatch<React.SetStateAction<any>>;
  // When the patient is being transferred the field is disabled and the
  // "+ Create new code" option is hidden. Defaults to false (e.g. Self Care).
  isPatientTransfer?: boolean;
}

/**
 * Shared ICD-10 / DSM diagnosis-code selector used by every clinic visit flow
 * (Physician/Nurse/Self/MentalHealth). Owns the master-code search/browse
 * dropdown and the inline "create code" dialog. Server search uses the backend
 * `diagnosisName` param, which ORs code + diagnosisName + description.
 */
// Don't fire the server search until at least this many characters are typed
// (reduces lag/noise on the large master-code catalog).
const MIN_SEARCH_CHARS = 3;

const DiagnosisCodeSelect = ({
  setSelectedDiagnosis,
  isPatientTransfer = false,
}: DiagnosisCodeSelectProps) => {
  const [pageCount, setPageCount] = useState(1);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState<any>([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [resetFlag, setResetFlag] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [openCodeDialog, setOpenCodeDialog] = useState(false);
  const [prefilledCodeSearch, setPrefilledCodeSearch] = useState("");

  useEffect(() => {
    if (!resetFlag) {
      const query = dropdownSearch?.trim() || "";
      if (query.length === 0) {
        getDropdownList(pageCount);
      } else if (query.length >= MIN_SEARCH_CHARS) {
        getDropdownSearchList(dropdownSearch);
      } else {
        // 1-2 chars: wait for more input (don't query the server)
        setDropdownOptions([]);
      }
    }
  }, [dropdownSearch, pageCount]);

  useEffect(() => {
    if (resetFlag) {
      resetList();
    }
  }, [resetFlag]);

  const resetList = () => {
    setSelectedValue(null);
    setDropdownSearch("");
    setPageCount(1);
    getDropdownList(1, true);
  };

  // Browse the master-code list, paginated (appends on scroll).
  const getDropdownList = async (pageCount: any, reset = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/mastercode`,
        {
          params: { pageNumber: pageCount, limit: 100 },
          headers: {
            Authorization: `Bearer ${JSON.parse(getToken() || "")}`,
          },
        },
      );
      const codes = response?.data?.data?.codes || [];
      const masterCodesOption = filterMasterCodeData(codes);
      if (masterCodesOption && masterCodesOption.length > 0) {
        if (reset) {
          setDropdownOptions(masterCodesOption);
          setResetFlag(false);
        } else if (
          !dropdownOptions?.some(
            (o: any) => o?.value === masterCodesOption[0]?.value,
          )
        ) {
          setDropdownOptions(dropdownOptions.concat(masterCodesOption));
        }
      } else if (reset) {
        setResetFlag(false);
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  // Server search: the backend `diagnosisName` param ORs code + diagnosisName
  // + description (case-insensitive), so this finds codes by code OR by
  // diagnosis text (e.g. "abdominal pain").
  const getDropdownSearchList = async (search: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/mastercode`,
        {
          params: { diagnosisName: search },
          headers: {
            Authorization: `Bearer ${JSON.parse(getToken() || "")}`,
          },
        },
      );
      const codes = response?.data?.data?.codes;
      if (codes) {
        setDropdownOptions(filterMasterCodeData(codes) || []);
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  // IF SEARCH VALUE  THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChange = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearch(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // ADD VALUE IN DIAGNOSIS
  const addDiagnosisToTheTable = (data: any) => {
    setSelectedDiagnosis((prevItems: any) => {
      // Check if the item is already in the array
      const alreadySelected = prevItems?.some(
        (item: any) => item?.label === data?.label,
      );

      const selectedCode = prevItems?.filter(
        (item: any) => item?.label !== data?.label,
      );

      if (selectedCode?.length > 4) {
        eventEHRCustomToastMessage({
          status: "error",
          title: "You can select maximum 5 codes",
        });
        return prevItems;
      }

      if (!alreadySelected) {
        return [...prevItems, data]; // Add if not already present
      }

      return prevItems; // Return unchanged if already present
    });
  };

  return (
    <>
      <EventEHRLazyDropdown
        clearSearch={true}
        filterOptions={(options) => options}
        isDisabled={isPatientTransfer}
        placeholder="Search ICD 10 or DSM Code"
        noOptionText={
          (dropdownSearch?.trim()?.length || 0) > 0 &&
          (dropdownSearch?.trim()?.length || 0) < MIN_SEARCH_CHARS
            ? "Type at least 3 characters to search"
            : "No options found"
        }
        dropdownOptions={dropdownOptions}
        dropdownSearch={dropdownSearch}
        fullWidth="350px"
        startIcon={
          <img
            alt="search-icon"
            src={searchIcon}
            width={"18px"}
            height={"18px"}
          />
        }
        selectedValue={selectedValue}
        setDropdownSearch={handleSearchedValueChange}
        setPageCount={(data) => {
          setPageCount(data);
        }}
        setResetFlag={(data) => {
          setResetFlag(data);
        }}
        setSelectedValue={(data) => {
          if (data) {
            addDiagnosisToTheTable(data);
          }
          setSelectedValue(data);
        }}
        showCreateOption={!isPatientTransfer}
        createOptionLabel={
          dropdownSearch ? `+ Create "${dropdownSearch}"` : "+ Create new code"
        }
        onCreateClick={(search) => {
          setPrefilledCodeSearch(search);
          setOpenCodeDialog(true);
        }}
      />
      {openCodeDialog && (
        <CodeDialog
          isOpen={openCodeDialog}
          handleClose={() => setOpenCodeDialog(false)}
          defaultCode={prefilledCodeSearch}
          onCreated={(newCode) => {
            const [shaped] = filterMasterCodeData([newCode]);
            if (shaped) {
              addDiagnosisToTheTable(shaped);
            }
            setOpenCodeDialog(false);
            setResetFlag(true);
          }}
        />
      )}
    </>
  );
};

export default DiagnosisCodeSelect;
